import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../services/api';
import { CheckCircle, XCircle, Eye, MapPin, Calendar, User, Image as ImageIcon } from 'lucide-react';
import { showInfo, showSuccess } from '../utils/toast';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling

interface PendingImage {
  id: number;
  image_url: string;
  display_order: number;
}

interface PendingFair {
  id: number;
  name: string;
  description: string | null;
  latitude: number | null;
  longitude: number | null;
  geolocation_source: string | null;
  status: string;
  submitted_at: string | null;
  submitter_email: string | null;
  submitter_display_name: string | null;
  images: PendingImage[];
  rejection_reason: string | null;
}

const PendingFairReview: React.FC = () => {
  const [pendingItems, setPendingItems] = useState<PendingFair[]>([]);
  const [selectedItem, setSelectedItem] = useState<PendingFair | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');

  // Approval form state
  const [approvalEmail, setApprovalEmail] = useState('');
  const [approvalPhone, setApprovalPhone] = useState('');

  // Rejection form state
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  const loadPendingFairs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = statusFilter !== 'all' ? { status_filter: statusFilter } : {};
      const response = await adminApi.getPendingFairs(params);
      setPendingItems(response.items || []);
    } catch (err: unknown) {
      console.error('Failed to load pending artist fairs:', err);
      const message = extractApiError(err).message;
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadPendingFairs();
  }, [loadPendingFairs]);

  const loadDetails = async (id: number) => {
    try {
      const response = await adminApi.getPendingFairById(id);
      setSelectedItem(response);
    } catch (err: unknown) {
      console.error('Failed to load details:', err);
      const message = extractApiError(err).message;
      setError(message);
    }
  };

  const handleApprove = async () => {
    if (!selectedItem) return;

    try {
      setActionLoading(true);
      setError(null);

      await adminApi.approvePendingFair(selectedItem.id, {
        email: approvalEmail.trim() || undefined,
        phone: approvalPhone.trim() || undefined,
        approved_product_ids: []
      });

      showSuccess('Fair approved successfully!');
      setSelectedItem(null);
      setApprovalEmail('');
      setApprovalPhone('');
      await loadPendingFairs();
    } catch (err: unknown) {
      console.error('Failed to approve:', err);
      const message = extractApiError(err).message;
      setError(message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedItem || !rejectionReason.trim()) {
      setError('Rejection reason is required (minimum 10 characters)');
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      await adminApi.rejectPendingFair(selectedItem.id, {
        rejection_reason: rejectionReason.trim()
      });

      showInfo('Fair rejected');
      setSelectedItem(null);
      setRejectionReason('');
      setShowRejectModal(false);
      await loadPendingFairs();
    } catch (err: unknown) {
      console.error('Failed to reject:', err);
      const message = extractApiError(err).message;
      setError(message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Pending Artist Fair Submissions</h1>
          <p className="text-gray-600">Review and approve community-submitted artist fairs</p>
        </div>

        {/* Status Filter */}
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => setStatusFilter('pending')}
            className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base ${
              statusFilter === 'pending'
                ? 'bg-yellow-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setStatusFilter('approved')}
            className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base ${
              statusFilter === 'approved'
                ? 'bg-primary text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Approved
          </button>
          <button
            onClick={() => setStatusFilter('rejected')}
            className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base ${
              statusFilter === 'rejected'
                ? 'bg-red-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Rejected
          </button>
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base ${
              statusFilter === 'all'
                ? 'bg-primary text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All
          </button>
        </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List of submissions */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">
                Submissions ({pendingItems.length})
              </h2>
            </div>
            <div className="divide-y divide-gray-200 max-h-[calc(100vh-300px)] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-500">Loading...</div>
              ) : pendingItems.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No {statusFilter !== 'all' ? statusFilter : ''} submissions
                </div>
              ) : (
                pendingItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => loadDetails(item.id)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                      selectedItem?.id === item.id ? 'bg-moonbone' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{item.name}</h3>
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <User className="h-4 w-4 mr-1" />
                          {item.submitter_display_name || item.submitter_email}
                        </div>
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <Calendar className="h-4 w-4 mr-1" />
                          {formatDate(item.submitted_at)}
                        </div>
                      </div>
                      <div>
                        {item.status === 'pending' && (
                          <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">
                            Pending
                          </span>
                        )}
                        {item.status === 'approved' && (
                          <span className="bg-teal-100 text-primary-dark text-xs px-2 py-1 rounded">
                            Approved
                          </span>
                        )}
                        {item.status === 'rejected' && (
                          <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
                            Rejected
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Details panel */}
        <div className="lg:col-span-2">
          {selectedItem ? (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedItem.name}</h2>
                  <div className="flex items-center text-gray-600 mt-2">
                    <User className="h-4 w-4 mr-1" />
                    <span>{selectedItem.submitter_display_name || selectedItem.submitter_email}</span>
                  </div>
                </div>
                <div>
                  {selectedItem.status === 'pending' && (
                    <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full">
                      Pending Review
                    </span>
                  )}
                  {selectedItem.status === 'approved' && (
                    <span className="bg-teal-100 text-primary-dark px-3 py-1 rounded-full">
                      Approved
                    </span>
                  )}
                  {selectedItem.status === 'rejected' && (
                    <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full">
                      Rejected
                    </span>
                  )}
                </div>
              </div>

              {/* Description */}
              {selectedItem.description && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Description</h3>
                  <p className="text-gray-600 whitespace-pre-wrap">{selectedItem.description}</p>
                </div>
              )}

              {/* Location */}
              {selectedItem.latitude && selectedItem.longitude && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Location</h3>
                  <div className="flex items-center text-gray-600">
                    <MapPin className="h-4 w-4 mr-2" />
                    <span>
                      {selectedItem.latitude.toFixed(6)}, {selectedItem.longitude.toFixed(6)}
                    </span>
                    <span className="ml-2 text-sm text-gray-500">
                      ({selectedItem.geolocation_source})
                    </span>
                  </div>
                </div>
              )}

              {/* Images */}
              {selectedItem.images && selectedItem.images.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <ImageIcon className="h-4 w-4 mr-1" />
                    Images ({selectedItem.images.length})
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {selectedItem.images.map((img) => (
                      <div key={img.id} className="aspect-square rounded-lg overflow-hidden border border-gray-200">
                        <img
                          src={img.image_url}
                          alt={`Fair image ${img.display_order + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rejection reason */}
              {selectedItem.status === 'rejected' && selectedItem.rejection_reason && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-red-900 mb-2">Rejection Reason</h3>
                  <p className="text-red-800">{selectedItem.rejection_reason}</p>
                </div>
              )}

              {/* Action buttons for pending items */}
              {selectedItem.status === 'pending' && (
                <div className="border-t border-gray-200 pt-6 mt-6">
                  <h3 className="text-lg font-semibold mb-4">Review Actions</h3>

                  {/* Optional contact info */}
                  <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email (optional)
                      </label>
                      <input
                        type="email"
                        value={approvalEmail}
                        onChange={(e) => setApprovalEmail(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="contact@fair.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone (optional)
                      </label>
                      <input
                        type="tel"
                        value={approvalPhone}
                        onChange={(e) => setApprovalPhone(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <button
                      onClick={handleApprove}
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center px-4 sm:px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                    >
                      <CheckCircle className="h-5 w-5 mr-2" />
                      {actionLoading ? 'Approving...' : 'Approve & Create Artist Fair'}
                    </button>
                    <button
                      onClick={() => setShowRejectModal(true)}
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center px-4 sm:px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                    >
                      <XCircle className="h-5 w-5 mr-2" />
                      Reject
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
              <Eye className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>Select a submission to review</p>
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Reject Submission</h3>
            <p className="text-gray-600 mb-4">
              Please provide a reason for rejecting this submission. The user will be notified.
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
              placeholder="Explain why this submission cannot be approved..."
              minLength={10}
              maxLength={1000}
            />
            <p className="text-sm text-gray-500 mb-4">
              {rejectionReason.length}/1000 characters (minimum 10)
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading || rejectionReason.length < 10}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingFairReview;
